import React from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { font, spacing } from '@mobi/component-library/Foundations'
import { FeatureFlags } from '@mobi/settings'
import { MainContainer } from '@core/Components/Containers'
import { useFeature } from '@core/Utils/hooks'
import { SettingsItemWithIcon } from './Components/Common/SettingItem/SettingItem'
import {
  HapticFeedbackSetting,
  isHapticsSupported,
  EnhancedBetslipSetting,
  PayoutNotification,
  KeypadDenominationsSetting,
  MFASettingsRow,
  KambiSportsOfferingIntegrationOverrideSetting,
} from './Components'
import { SettingsHeader } from './SettingsHeader'
import { DynamicsPushTestSettings } from './Components/DynamicsPushTestSettings'
import { routes as accountRoutes } from '@mobi/account/Utils'

export const Settings: React.FC = () => {
  const navigate = useNavigate()

  const isDynamicsPushDeveloperModeActive = useFeature(
    FeatureFlags.DYNAMICS_PUSH_DEVELOPER_MODE.key
  )

  const isKambiSportsOfferingIntegrationFeatureActive = useFeature(
    FeatureFlags.KAMBI_SPORTS_OFFERING_INTEGRATION.key
  )

  const isAmlContactDetailsEnabled = useFeature(FeatureFlags.AML_CONTACT_DETAILS.key)
  const shouldRenderHapticsSetting = isHapticsSupported

  return (
    <MainContainer data-tid-settings-container>
      <SettingsHeader title='Settings' />
      <SettingsGroup title='Security'>
        <MFASettingsRow />

        <SettingsItemWithIcon
          label='Edit Personal Details'
          description='Email & Mobile Phone Number'
          iconName='LineUsersEdit'
          onClick={() =>
            navigate(
              isAmlContactDetailsEnabled ? accountRoutes.personalDetails : '/account/contactdetails'
            )
          }
        />
        <SettingsItemWithIcon
          label='Change Password'
          description='*********'
          iconName='LinePasscodeLock'
          onClick={() => navigate('/account/change-password')}
        />
      </SettingsGroup>
      <SettingsGroup title='General'>
        {isDynamicsPushDeveloperModeActive && <DynamicsPushTestSettings />}
        <EnhancedBetslipSetting />
        {shouldRenderHapticsSetting && <HapticFeedbackSetting />}
        {isReactNativeApp && <PayoutNotification />}
        {isKambiSportsOfferingIntegrationFeatureActive && (
          <KambiSportsOfferingIntegrationOverrideSetting />
        )}
        <KeypadDenominationsSetting />
      </SettingsGroup>
    </MainContainer>
  )
}

// ===============
// Local Component
// ===============

const SettingsGroup: React.FC<{ children: React.ReactNode; title: string }> = ({
  children,
  title,
}) => {
  return (
    <SettingsGroupContainerStyled>
      <SettingsGroupHeaderStyled>{title}</SettingsGroupHeaderStyled>
      <div>{children}</div>
    </SettingsGroupContainerStyled>
  )
}

const SettingsGroupContainerStyled = styled.section({
  marginLeft: spacing.big,
  marginRight: spacing.big,

  '&:first-of-type': {
    marginTop: spacing.big,
  },
})

const SettingsGroupHeaderStyled = styled.h2({
  ...font.size.lg,
  fontWeight: font.weight.semibold,
  color: 'black',
  textAlign: 'left',
  textTransform: 'none',
})
